import React, { useEffect, useId, useImperativeHandle, useLayoutEffect, useRef } from 'react';
import gsap from 'gsap';
import useWindowSize from '@/hooks/useWindowSize';
import styles from 'components/blocks/header-rotating-shape/HeaderRotatingShape.module.scss';
import classNames from 'classnames';

const BACKGROUND_ICON_VIEWBOX_SIZE = 15000;
const BACKGROUND_ICON_CENTER = BACKGROUND_ICON_VIEWBOX_SIZE / 2;
const DEFAULT_ROTATION_DURATION = 180;

type BackgroundLargeCurveProps = {
  iconColor?: string;
  isVisible?: boolean;
  initialRotation?: number;
  position?: Pick<React.CSSProperties, 'top' | 'right' | 'bottom' | 'left'>;
  revealDelay?: number;
  ref?: React.Ref<BackgroundLargeCurveRef>;
};

export type BackgroundLargeCurveRef = {
  elementRef: React.RefObject<SVGSVGElement | null>;
  revealTimelineRef: React.RefObject<gsap.core.Timeline>;
};

const defaultPosition: NonNullable<BackgroundLargeCurveProps['position']> = {
  top: '50',
  left: '50%',
};

const BackgroundLargeCurve = ({
  iconColor = '#fff',
  isVisible,
  initialRotation = 0,
  position = defaultPosition,
  revealDelay = 1.5,
  ref,
}: BackgroundLargeCurveProps) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const backgroundIconMaskId = useId();
  const bgIconRef = useRef<SVGGElement>(null);
  const bgIconMaskRef = useRef<SVGRectElement>(null);
  const revealTimelineRef = useRef<gsap.core.Timeline>(gsap.timeline({ paused: true }));
  const bgRectRef = useRef<SVGRectElement>(null);
  const rotationTweenRef = useRef<gsap.core.Tween>(null);
  const rotationSpeedTweenRef = useRef<gsap.core.Tween>(null);
  const scaleYTweenRef = useRef<gsap.core.Tween>(null);

  const { width: windowWidth, height: windowHeight } = useWindowSize();

  const rectangleWidth = 2 * windowWidth;
  const rectangleHeight = (100 * windowHeight) / 80;

  useLayoutEffect(() => {
    if (!bgIconMaskRef.current || !bgRectRef.current || !bgIconRef.current) return undefined;

    const revealTimeline = revealTimelineRef.current;
    revealTimeline.clear();
    revealTimeline
      .fromTo(
        [bgRectRef.current, bgIconRef.current],
        { opacity: 0 },
        {
          opacity: 1,
          duration: 0.1,
          ease: 'power2.in',
        },
        0,
      )
      .fromTo(
        bgIconMaskRef.current,
        {
          attr: {
            rx: '50%',
            ry: '50%',
          },
          '--mask-scale': 0,
        },
        {
          attr: {
            rx: 0,
            ry: 0,
          },
          '--mask-scale': 1,
          duration: 0.8,
          ease: 'power2.inOut',
        },
        revealDelay,
      )
      .pause(0);

    return () => {
      revealTimeline.clear();
    };
  }, [revealDelay, windowWidth, windowHeight]);

  useEffect(() => {
    if (!bgIconRef.current) return undefined;

    gsap.set(bgIconRef.current, { rotation: initialRotation, transformOrigin: '50% 50%' });

    rotationTweenRef.current = gsap.to(bgIconRef.current, {
      rotation: '+=360',
      duration: DEFAULT_ROTATION_DURATION,
      ease: 'none',
      repeat: -1,
      transformOrigin: '50% 50%',
    });

    scaleYTweenRef.current = gsap.fromTo(
      bgIconRef.current,
      { scale: 0.65 },
      {
        scale: 0.3,
        duration: DEFAULT_ROTATION_DURATION / 4,
        ease: 'none',
        repeat: -1,
        yoyo: true,
        transformOrigin: '50% 50%',
      },
    );

    return () => {
      scaleYTweenRef.current?.kill();
      scaleYTweenRef.current = null;
      rotationSpeedTweenRef.current?.kill();
      rotationSpeedTweenRef.current = null;
      rotationTweenRef.current?.kill();
      rotationTweenRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (typeof isVisible !== 'boolean') return undefined;

    if (isVisible) {
      revealTimelineRef.current?.play();
    } else {
      revealTimelineRef.current?.reverse();
    }
    return undefined;
  }, [isVisible]);

  useImperativeHandle(
    ref,
    () => ({
      elementRef: svgRef,
      revealTimelineRef,
    }),
    [],
  );

  return (
    <svg
      width={BACKGROUND_ICON_VIEWBOX_SIZE}
      height={BACKGROUND_ICON_VIEWBOX_SIZE}
      viewBox={`0 0 ${BACKGROUND_ICON_VIEWBOX_SIZE} ${BACKGROUND_ICON_VIEWBOX_SIZE}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={classNames(styles.backgroundIcon, styles.largeCurve)}
      style={position}
      ref={svgRef}
    >
      <defs>
        <clipPath id={backgroundIconMaskId} clipPathUnits="userSpaceOnUse">
          <rect
            ref={bgIconMaskRef}
            className={styles.backgroundIconMask}
            style={{ '--mask-scale': 0 } as React.CSSProperties}
            width={rectangleWidth}
            height={rectangleHeight}
            x={BACKGROUND_ICON_CENTER - rectangleWidth / 2}
            y={BACKGROUND_ICON_CENTER - rectangleHeight / 2}
          />
        </clipPath>
      </defs>
      <g clipPath={`url(#${backgroundIconMaskId})`}>
        <g ref={bgIconRef} className={styles.backgroundIconSpin}>
          <rect
            width={BACKGROUND_ICON_VIEWBOX_SIZE}
            height={BACKGROUND_ICON_VIEWBOX_SIZE}
            fill={iconColor}
            ref={bgRectRef}
          />
          <path
            d="M7500.02 7500.02C9215.75 9215.75 9215.75 11997.5 7500.02 13713.2C5784.28 15429 3002.53 15429 1286.79 13713.2"
            fill="var(--background-color)"
          />
          <path
            d="M7500.02 7500.05C5784.28 5784.32 5784.28 3002.57 7500.02 1286.83C9215.75 -428.897 11997.5 -428.897 13713.2 1286.84"
            fill="var(--background-color)"
          />
          <path
            d="M1286.87 13713.2C-428.866 11997.5 -428.866 9215.71 1286.87 7499.98C3002.61 5784.25 5784.36 5784.25 7500.1 7499.98"
            fill="var(--background-color)"
          />
          <path
            d="M7499.29 7500.32C9215.02 9216.06 11996.8 9216.06 13712.5 7500.32C15428.2 5784.59 15428.2 3002.83 13712.5 1287.1"
            fill="var(--background-color)"
          />
          <path
            d="M7500.03 7500.04C5784.29 9215.77 3002.54 9215.77 1286.8 7500.04C-428.934 5784.3 -428.934 3002.56 1286.8 1286.82"
            fill="var(--background-color)"
          />
          <path
            d="M13713.2 13713.2C11997.4 15428.9 9215.67 15428.9 7499.94 13713.2C5784.2 11997.4 5784.2 9215.69 7499.94 7499.96"
            fill="var(--background-color)"
          />
        </g>
      </g>
    </svg>
  );
};

export default BackgroundLargeCurve;
